/**
 * The move to mzapps.cloud (27.10.2026, docs/08-offene-fragen.md): during the transition each app
 * answers at its old and its new address, with the same data. At the old one a hint names the new
 * one; a sign-in there carries over to the new address („Anmeldung mitnehmen“, handover.ts on the
 * server), where the app asks once for the password so the password manager saves it for the new
 * address too.
 */
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { account, serverFeatures } from "../account.ts";
import type { SignedIn } from "../account.ts";
import { APP } from "../brand.ts";
import { useStore } from "../hooks.ts";
import { ApiError, store } from "../store.ts";
import { AuthPage, Login, messageOf } from "./Login.tsx";

/** The address without its scheme, as people read it. */
export const hostOf = (origin: string) => origin.replace(/^https?:\/\//, "");

/** The app on the home screen: it stays at its address, a browser tab moves on. */
const standalone = () => matchMedia("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;

/** Opens the new address signed in, with a key for 60 seconds. */
async function handOver(): Promise<void> {
  location.assign((await account.handover()).url);
}

/** Changes this device still has to send: they go out from this address only, so it stays until they are sent. */
const waiting = () => store.replica?.pending.length ?? 0;

/**
 * After a sign-in at an old address in the browser: on to the new one, signed in. The app on the
 * home screen stays (it shows the hint), so does a device with changes still to send, and so does
 * everything when the handover fails.
 */
export async function signedInHere(s: SignedIn, stay: (s: SignedIn) => void): Promise<void> {
  const f = await serverFeatures().catch(() => null);
  if (f?.movedTo && !standalone() && waiting() === 0) {
    try {
      return await handOver();
    } catch {
      // Signed in all the same: here then.
    }
  }
  stay(s);
}

/** „<App> ist umgezogen“ at the old address, above the app; an account's device can go over signed in. */
export function MovedBanner() {
  const { store: shown } = useStore();
  const [to, setTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    serverFeatures().then((f) => setTo(f.movedTo), () => undefined);
  }, []);
  if (!to) return null;
  const accountDevice = !!shown.config && !shown.config.token;
  const pending = shown.replica?.pending.length ?? 0;
  const go = () => handOver().catch((err: unknown) => setError(messageOf(err)));
  return (
    <div className="banner moved-banner" data-testid="moved-banner">
      <span>
        {error ?? (
          <>
            {APP.short} ist umgezogen:{" "}
            <a href={to} target="_blank" rel="noopener">
              {hostOf(to)}
            </a>
            . Bitte dort anmelden und neu zum Home-Bildschirm hinzufügen. Deine Daten sind dort dieselben.
            {pending > 0 && " Was hier noch wartet, sendet die App von dieser Adresse aus; lass sie offen, bis oben „aktuell“ steht."}
          </>
        )}
      </span>
      {accountDevice && (
        <span className="buttons">
          <button onClick={go} disabled={pending > 0} data-testid="moved-go">
            Angemeldet wechseln
          </button>
        </span>
      )}
    </div>
  );
}

/**
 * #/mitnehmen at the old address, from the button on the new address's sign-in page: signed in
 * here, straight on; otherwise the sign-in as ever, where the password manager fills in.
 */
export function TakeAlong({ onSignedIn }: { onSignedIn: (s: SignedIn) => void }) {
  const [phase, setPhase] = useState<"busy" | "sign_in" | { error: string }>("busy");
  useEffect(() => {
    handOver().catch((err: unknown) => {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) setPhase("sign_in");
      else if (err instanceof ApiError && err.status === 404) location.hash = "#/";
      else setPhase({ error: messageOf(err) });
    });
  }, []);
  if (phase === "sign_in") {
    return (
      <Login
        context={`Melde dich hier an wie gewohnt. Danach bist du unter der neuen Adresse von ${APP.short} angemeldet.`}
        onSignedIn={(s) => handOver().catch(() => onSignedIn(s))}
      />
    );
  }
  return (
    <AuthPage>
      {phase === "busy" ? (
        <p className="muted">Weiter zur neuen Adresse …</p>
      ) : (
        <div className="card">
          <p className="error-box">{phase.error}</p>
          <a className="button wide center" href="#/">
            Weiter
          </a>
        </div>
      )}
    </AuthPage>
  );
}

/** One redemption per key, also when the page renders twice. */
const redeemed = new Map<string, Promise<SignedIn>>();

/**
 * #/uebergabe/<key> at the new address: signs in with the key, then „Passwort einmal eingeben“ so
 * the password manager saves the password for this address; skipping is fine.
 */
export function Handover({ handoverKey, onSignedIn }: { handoverKey: string; onSignedIn: (s: SignedIn) => void }) {
  const [phase, setPhase] = useState<"busy" | { error: string } | { signed: SignedIn }>("busy");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [state, setState] = useState<{ error?: string; busy?: boolean }>({});

  useEffect(() => {
    if (!redeemed.has(handoverKey)) redeemed.set(handoverKey, account.redeemHandover(handoverKey));
    // The key is used up; out of the address bar and the history with it.
    history.replaceState(null, "", `${location.pathname}${location.search}#/uebergabe`);
    redeemed.get(handoverKey)!.then(
      (s) => [setUsername(s.user.username), setPhase({ signed: s })],
      (err: unknown) => setPhase({ error: messageOf(err) }),
    );
  }, [handoverKey]);

  const next = (s: SignedIn) => {
    location.hash = "#/";
    onSignedIn(s);
  };

  if (phase === "busy") return <AuthPage>{<p className="muted">Anmeldung wird übernommen …</p>}</AuthPage>;
  if ("error" in phase) {
    return (
      <AuthPage>
        <div className="card">
          <p className="error-box" data-testid="handover-error">
            {phase.error}
          </p>
          <a className="button wide center" href="#/anmelden">
            Zur Anmeldung
          </a>
        </div>
      </AuthPage>
    );
  }
  const signed = phase.signed;
  async function save(e: FormEvent) {
    e.preventDefault();
    if (!password) return setState({ error: "Bitte gib dein Passwort ein." });
    setState({ busy: true });
    try {
      await account.checkPassword(password);
      next(signed);
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  return (
    <AuthPage>
      <div className="card" data-testid="handover-save">
        <h2>Passwort einmal eingeben</h2>
        <p className="ok-text">
          Du bist angemeldet als <b>{signed.user.displayName}</b>.
        </p>
        <p>Damit dein Passwort-Manager dich hier wiedererkennt: Gib dein Passwort einmal ein. Er bietet dann an, es für diese Adresse zu speichern.</p>
        <form noValidate onSubmit={save}>
          <label>
            Benutzername
            <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="handover-username" />
          </label>
          <label>
            Passwort
            <input type="password" value={password} onChange={(e) => [setPassword(e.target.value), setState({})]} autoComplete="current-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="handover-password" />
          </label>
          {state.error && <p className="error-box">{state.error}</p>}
          <button className="primary" disabled={state.busy} data-testid="handover-check">
            {state.busy ? "Prüfen …" : "Passwort speichern"}
          </button>
        </form>
        <button className="link" onClick={() => next(signed)} data-testid="handover-skip">
          Überspringen
        </button>
      </div>
    </AuthPage>
  );
}
